
import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { FiArrowRight } from 'react-icons/fi';

const Pricing = () => {
  const plans = [
    {
      name: 'Architectural Stills',
      price: '500',
      unit: 'per image',
      isStartingAt: true,
      description: 'High-fidelity photorealistic renderings for exterior and interior concepts.',
      features: ['High Resolution', 'Advanced Lighting', 'Environment Integration', '2 Revision Rounds']
    },
    {
      name: 'Dimensional Layouts',
      price: '250',
      unit: 'per image',
      isStartingAt: true,
      description: 'Engaging 3D floor plans that provide clear spatial understanding of your project.',
      features: ['Fully Furnished', 'Material Accuracy', 'Top-down & Isometric', 'Fast Turnaround']
    },
    {
      name: 'Cinematic Walkthroughs',
      price: '1,000',
      unit: 'per 60 seconds',
      isStartingAt: true,
      description: 'Immersive motion experiences that guide viewers through unbuilt spaces.',
      features: ['Cinematic Visual Quality', 'Fluid Camera Paths', 'Sound Design Options', 'Dynamic Lighting']
    }
  ];

  return (
    <section id="pricing" className="py-24 md:py-32 bg-neutral-950 text-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div 
          className="text-center max-w-3xl mx-auto mb-20"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="text-neutral-400 uppercase tracking-widest text-sm mb-4 block">Investment</span>
          <h2 className="text-4xl md:text-5xl font-light">Transparent <span className="font-serif italic font-semibold">Value</span></h2>
          <p className="mt-6 text-neutral-400 font-light text-lg">
            Premium visualization services tailored to bring your architectural vision to market.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {plans.map((plan, index) => (
            <motion.div
              key={plan.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="bg-neutral-900/50 border border-neutral-800 p-8 md:p-10 flex flex-col hover:border-white/20 transition-all duration-500 group"
            >
              <h3 className="text-xl font-medium mb-2">{plan.name}</h3>
              <div className="mb-6">
                {plan.isStartingAt && (
                  <span className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 block mb-1">Starting at</span>
                )}
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl font-serif italic">$</span>
                  <span className="text-5xl font-light tracking-tighter">{plan.price}</span>
                  <span className="text-neutral-500 text-[10px] uppercase tracking-widest ml-1 self-center mt-2">USD</span>
                  <span className="text-neutral-500 text-sm ml-2">{plan.unit}</span>
                </div>
              </div>
              
              <p className="text-neutral-400 font-light mb-8 text-sm leading-relaxed">
                {plan.description}
              </p>

              <ul className="space-y-4 mb-10 flex-grow">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-center gap-3 text-sm text-neutral-300 font-light">
                    <div className="w-1 h-1 bg-white/40 rounded-full" />
                    {feature}
                  </li>
                ))}
              </ul>

              <a 
                href="#contact" 
                className="flex items-center justify-center gap-3 w-full border border-white/10 py-4 text-xs uppercase tracking-widest group-hover:bg-white group-hover:text-black transition-all duration-300"
              >
                Inquire Now
                <SafeIcon icon={FiArrowRight} className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
              </a>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Pricing;
